import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronDown, Layers, Plus, ScanLine, Tag } from 'lucide-react';
import { toast } from 'sonner';
import type { Asset, HolderInput } from '@khadi/shared';
import { api, ApiError } from '../lib/api';
import { useCategories, useManufacturers, useModels, useSuppliers } from '../lib/queries';
import { PageHeader, cn } from '../components/ui';
import { HolderPicker, isHolderComplete } from '../components/HolderPicker';
import { ScannerSheet } from '../components/ScannerSheet';
import { LabelSheet } from '../components/LabelSheet';
import { ModelCreateDialog } from '../components/ModelCreateDialog';

/**
 * Schnellerfassung — der Vorgang, den das Snipe-Log 153 Mal zeigt und
 * fast immer mit einer Übergabe in derselben Sekunde.
 *
 * Deshalb: ein Bildschirm für beides. Seriennummer, Modell, Ziel, fertig.
 * Die Inventarnummer vergibt das System.
 */
export function CapturePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [serial, setSerial] = useState('');
  const [modelId, setModelId] = useState<number>(0);
  const [modelSearch, setModelSearch] = useState('');
  const [target, setTarget] = useState<HolderInput>({ type: 'employee', id: 0 } as HolderInput);
  const [showDetails, setShowDetails] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchaseCost, setPurchaseCost] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [supplierId, setSupplierId] = useState<number>(0);
  const [datevId, setDatevId] = useState('');
  const [notes, setNotes] = useState('');

  const [scannerOpen, setScannerOpen] = useState(false);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [labelForAsset, setLabelForAsset] = useState<number | null>(null);
  const serialRef = useRef<HTMLInputElement>(null);

  const models = useModels();
  const suppliers = useSuppliers();
  const categories = useCategories();
  const manufacturers = useManufacturers();

  const nextTag = useQuery({
    queryKey: ['next-tag'],
    queryFn: () => api<{ asset_tag: string }>('/assets/next-tag'),
  });

  const matchingModels = models.data?.rows.filter((model) =>
    modelSearch.trim()
      ? `${model.name} ${model.manufacturer?.name ?? ''} ${model.category?.name ?? ''}`
          .toLowerCase()
          .includes(modelSearch.trim().toLowerCase())
      : true,
  );

  const selectedModel = models.data?.rows.find((model) => model.id === modelId);

  const create = useMutation({
    mutationFn: () =>
      api<Asset>('/assets', {
        method: 'POST',
        body: {
          serial: serial.trim(),
          model_id: modelId,
          holder: isHolderComplete(target) ? target : { type: 'none' },
          purchase_date: purchaseDate || undefined,
          purchase_cost: purchaseCost ? Number(purchaseCost) : undefined,
          order_number: orderNumber.trim() || undefined,
          supplier_id: supplierId || undefined,
          datev_id: datevId.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['next-tag'] });
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Das Gerät konnte nicht angelegt werden'),
  });

  /** Nach dem Speichern bleiben Modell und Ziel stehen — für Sammellieferungen. */
  function resetForNext(): void {
    setSerial('');
    setDatevId('');
    setNotes('');
    serialRef.current?.focus();
  }

  async function submit(mode: 'detail' | 'next' | 'label'): Promise<void> {
    const asset = await create.mutateAsync();
    toast.success(`${asset.asset_tag} angelegt`, {
      description: asset.holder ? `Übergeben an ${asset.holder.name}` : 'Liegt im Lager',
    });

    if (mode === 'next') {
      resetForNext();
      return;
    }
    if (mode === 'label') {
      setLabelForAsset(asset.id);
      resetForNext();
      return;
    }
    void navigate({ to: '/geraete/$assetId', params: { assetId: String(asset.id) } });
  }

  useEffect(() => {
    serialRef.current?.focus();
  }, []);

  const ready = serial.trim().length > 0 && modelId > 0;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        eyebrow="Neuzugang"
        title="Gerät erfassen"
        subtitle={
          nextTag.data ? (
            <>
              Bekommt die Inventarnummer{' '}
              <strong className="tabular text-[var(--color-fg1)]">{nextTag.data.asset_tag}</strong>
            </>
          ) : (
            'Seriennummer, Modell, Empfänger — mehr braucht es nicht.'
          )
        }
        actions={
          <Link to="/erfassen/mehrere" className="btn btn-ghost btn-md no-underline">
            <Layers className="h-4 w-4" aria-hidden />
            Mehrere
          </Link>
        }
      />

      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          void submit('detail');
        }}
      >
        {/* ---------- Seriennummer ---------- */}
        <section className="surface animate-fade-up pad">
          <label className="label" htmlFor="capture-serial">
            Seriennummer
          </label>
          <div className="flex gap-2">
            <input
              id="capture-serial"
              ref={serialRef}
              className="field tabular"
              value={serial}
              onChange={(event) => setSerial(event.target.value)}
              placeholder="z. B. CJQ0LHG6WR"
              autoCapitalize="characters"
              autoComplete="off"
              required
            />
            <button
              type="button"
              onClick={() => setScannerOpen(true)}
              className="btn btn-ghost btn-md shrink-0"
              aria-label="Seriennummer scannen"
            >
              <ScanLine className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </section>

        {/* ---------- Modell ---------- */}
        <section className="surface animate-fade-up pad" style={{ animationDelay: '40ms' }}>
          <div className="mb-2 flex items-center justify-between">
            <span className="label mb-0">Modell</span>
            <button
              type="button"
              onClick={() => setModelDialogOpen(true)}
              className="btn btn-quiet btn-sm"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Neues Modell
            </button>
          </div>

          {selectedModel ? (
            <div className="flex items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--color-khadi-forest-400)] bg-[var(--color-khadi-forest-100)] px-4 py-3.5">
              <span className="min-w-0">
                <span className="block truncate text-[0.9375rem] font-semibold">
                  {selectedModel.name}
                </span>
                <span className="block truncate text-sm text-[var(--color-fg3)]">
                  {selectedModel.manufacturer?.name} · {selectedModel.category?.name}
                </span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setModelId(0);
                  setModelSearch('');
                }}
                className="btn btn-quiet btn-sm shrink-0"
              >
                ändern
              </button>
            </div>
          ) : (
            <>
              <input
                className="field mb-2"
                value={modelSearch}
                onChange={(event) => setModelSearch(event.target.value)}
                placeholder="Modell suchen"
                aria-label="Modell suchen"
              />
              <div className="max-h-48 overflow-y-auto rounded-[var(--radius-md)] border border-[var(--color-line)]">
                {matchingModels?.length === 0 && (
                  <p className="px-3 py-4 text-center text-sm text-[var(--color-fg3)]">
                    Kein Modell gefunden — oben ein neues anlegen.
                  </p>
                )}
                {matchingModels?.slice(0, 40).map((model) => (
                  <button
                    key={model.id}
                    type="button"
                    onClick={() => setModelId(model.id)}
                    className="flex w-full items-center justify-between gap-2 px-5 py-4 text-left text-sm transition-colors hover:bg-[var(--color-bg-tinted)]"
                  >
                    <span className="truncate">{model.name}</span>
                    <span className="shrink-0 text-sm text-[var(--color-fg3)]">
                      {model.category?.name}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>

        {/* ---------- Ziel ---------- */}
        <section className="surface animate-fade-up pad" style={{ animationDelay: '80ms' }}>
          <span className="label">Übergeben an</span>
          <HolderPicker value={target} onChange={setTarget} />
        </section>

        {/* ---------- Optionales ---------- */}
        <section className="surface animate-fade-up overflow-hidden" style={{ animationDelay: '120ms' }}>
          <button
            type="button"
            onClick={() => setShowDetails((value) => !value)}
            className="flex w-full items-center justify-between px-5 py-4 text-left"
            aria-expanded={showDetails}
          >
            <span className="text-sm font-semibold text-[var(--color-fg1)]">
              Kaufdaten und Datev
              <span className="ml-2 font-normal text-[var(--color-fg3)]">optional</span>
            </span>
            <ChevronDown
              className={cn('h-4 w-4 transition-transform duration-200', showDetails && 'rotate-180')}
              aria-hidden
            />
          </button>

          {showDetails && (
            <div className="animate-fade-up grid gap-4 border-t border-[var(--color-line)] px-5 py-4 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="capture-date">
                  Kaufdatum
                </label>
                <input
                  id="capture-date"
                  type="date"
                  className="field"
                  value={purchaseDate}
                  onChange={(event) => setPurchaseDate(event.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="capture-cost">
                  Kaufpreis (€)
                </label>
                <input
                  id="capture-cost"
                  type="number"
                  step="0.01"
                  min="0"
                  className="field tabular"
                  value={purchaseCost}
                  onChange={(event) => setPurchaseCost(event.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="capture-supplier">
                  Lieferant
                </label>
                <select
                  id="capture-supplier"
                  className="field"
                  value={supplierId}
                  onChange={(event) => setSupplierId(Number(event.target.value))}
                >
                  <option value={0}>— keiner —</option>
                  {suppliers.data?.rows.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label" htmlFor="capture-order">
                  Bestellnummer
                </label>
                <input
                  id="capture-order"
                  className="field"
                  value={orderNumber}
                  onChange={(event) => setOrderNumber(event.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="capture-datev">
                  Datev-Anlagengut
                </label>
                <input
                  id="capture-datev"
                  className="field tabular"
                  value={datevId}
                  onChange={(event) => setDatevId(event.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label" htmlFor="capture-notes">
                  Notizen
                </label>
                <textarea
                  id="capture-notes"
                  rows={2}
                  className="field"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>
            </div>
          )}
        </section>

        {/* ---------- Abschluss ---------- */}
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => void submit('label')}
            disabled={!ready || create.isPending}
            className="btn btn-primary btn-lg flex-1"
          >
            <Tag className="h-4 w-4" aria-hidden />
            Speichern &amp; Etikett
          </button>
          <button
            type="button"
            onClick={() => void submit('next')}
            disabled={!ready || create.isPending}
            className="btn btn-ghost btn-lg flex-1"
          >
            Speichern &amp; nächstes
          </button>
        </div>

        <button
          type="submit"
          disabled={!ready || create.isPending}
          className="btn btn-quiet btn-md w-full"
        >
          {create.isPending ? 'Wird angelegt …' : 'Speichern und Gerät ansehen'}
        </button>
      </form>

      {scannerOpen && (
        <ScannerSheet
          title="Seriennummer scannen"
          onClose={() => setScannerOpen(false)}
          onResult={(code) => {
            setSerial(code.trim());
            serialRef.current?.focus();
          }}
        />
      )}

      {modelDialogOpen && (
        <ModelCreateDialog
          categories={categories.data?.rows ?? []}
          manufacturers={manufacturers.data?.rows ?? []}
          defaultName={modelSearch}
          onClose={() => setModelDialogOpen(false)}
          onCreated={(model) => {
            setModelId(model.id);
            setModelDialogOpen(false);
          }}
        />
      )}

      {labelForAsset && (
        <LabelSheet assetIds={[labelForAsset]} onClose={() => setLabelForAsset(null)} />
      )}
    </div>
  );
}
